export const socialStudio = {
  menu: { label: "Social Studio" },
  pageTitle: "Social Studio",
  subtitle: "Plan, schedule and manage your Instagram posts and stories in one place.",
  cta: {
    newPost: "New post",
    newStory: "New story",
    publishNow: "Publish now",
    schedule: "Schedule",
    saveDraft: "Save as draft",
    save: "Save",
    cancel: "Cancel",
    back: "Back",
    delete: "Delete",
    edit: "Edit",
    duplicate: "Duplicate",
    aiRewrite: "AI rewrite",
    suggestTime: "Best time",
    accept: "Accept",
    discard: "Discard",
    apply: "Apply",
    addToSeries: "Add to series",
    askAi: "Ask AI",
  },
  tabs: {
    feed: "Feed",
    stories: "Stories",
    calendar: "Calendar",
    drafts: "Drafts",
    inspiration: "Ideas",
  },
  status: {
    published: "Published",
    scheduled: "Scheduled",
    draft: "Draft",
  },
  stats: {
    scheduled: "Scheduled",
    activeStories: "Active stories",
    weeklyReach: "Weekly reach",
    engagement: "Engagement",
    nextPublish: "Next post",
    posted: "Posted this week",
  },
  feed: {
    title: "Feed overview",
    sectionPast: "Past posts",
    sectionScheduled: "Scheduled posts",
    sectionDrafts: "Drafts",
    empty: "No content yet.",
    likes: "likes",
    comments: "comments",
    saves: "saves",
    reach: "reach",
  },
  stories: {
    title: "Stories",
    series: "Series",
    addToSeries: "Add to series",
    seriesEmpty: "No active story series yet.",
    duration: "Duration",
    seconds: "s",
  },
  calendar: {
    title: "Content calendar",
    monthly: "Monthly",
    weekly: "Weekly",
    list: "List",
    today: "Today",
    dragHint: "Drag a card to another day to reschedule",
    filters: {
      all: "All",
      posts: "Posts",
      stories: "Stories",
      drafts: "Drafts",
    },
    drawerTitle: "Content for this day",
    noContent: "No scheduled content for this day.",
  },
  editor: {
    postTitle: "Post editor",
    storyTitle: "Story editor",
    sections: {
      media: "Media",
      caption: "Caption",
      hashtags: "Hashtags",
      location: "Location",
      tagging: "Tagging",
      schedule: "Schedule",
      crossPost: "Cross-post",
      pillar: "Content pillar",
    },
    media: {
      addFromLibrary: "Add from library",
      upload: "Upload",
      generateAi: "Generate with AI",
      reorderHint: "Drag to reorder",
      ratio: "Aspect ratio",
    },
    caption: {
      placeholder: "Write your caption...",
      counter: "{{count}}/2200 chars",
      aiButton: "Generate caption with AI",
      aiPromptPlaceholder: "e.g. morning routine at the studio",
      tone: "Tone",
      tones: { informal: "Informal", professional: "Professional", playful: "Playful", inspirational: "Inspirational" },
    },
    hashtags: {
      placeholder: "Add hashtag...",
      suggest: "AI suggest",
      groups: "Saved groups",
      saturation: "Saturation",
    },
    location: { placeholder: "Search location..." },
    tagging: { placeholder: "@username" },
    schedule: {
      now: "Publish now",
      later: "Schedule for later",
      draft: "Save as draft",
      pickDate: "Pick a date",
      pickTime: "Time",
      bestTime: "Best time",
      bestTimeHint: "AI suggestion based on follower activity",
    },
    crossPost: { hint: "Post will appear on the selected platforms" },
    pillar: { placeholder: "Select a content pillar..." },
  },
  story: {
    canvas: { hint: "Click an element to edit, drag on canvas to move" },
    addSticker: "Add sticker",
    stickers: {
      text: "Text",
      poll: "Poll",
      question: "Question",
      slider: "Slider",
      location: "Location",
      mention: "Mention",
      link: "Link",
      cta: "CTA",
      countdown: "Countdown",
    },
    background: { change: "Change background", upload: "Upload your own" },
    series: { title: "Story series", addNew: "+ New story to series" },
  },
  ai: {
    drawerTitle: "AI editor",
    subtitle: "Refine your content with one click.",
    actions: {
      moreCasual: "Make it casual",
      shorter: "Make it shorter",
      addEmoji: "Add emojis",
      addHashtags: "Suggest hashtags",
      translateEn: "Translate to English",
      abVariant: "Generate A/B variant",
      makePunchy: "Make it punchier",
      addCta: "Add a CTA at the end",
    },
    chatPlaceholder: "Tell me what to change...",
    generating: "Generating...",
    diff: { original: "Original", suggestion: "Suggestion" },
    imageGen: {
      title: "AI image generation",
      promptPlaceholder: "Describe the image...",
      generate: "Generate variations",
      generating: "Generating...",
    },
  },
  bestTime: {
    title: "Best posting times",
    subtitle: "Your followers' activity over the last 30 days",
    legend: { low: "Low", high: "High" },
    days: ["M", "T", "W", "T", "F", "S", "S"],
  },
  pillars: {
    title: "Content pillars",
    subtitle: "How your content is distributed across topics",
    target: "Target",
    actual: "Actual",
    overweight: "Overweighted",
    balanced: "Balanced",
  },
  hashtags: {
    title: "Hashtag groups",
    add: "New group",
    insert: "Insert",
  },
  templates: {
    title: "Templates",
    subtitle: "Start here — one click and the post is preloaded",
    use: "Use it",
    categories: {
      all: "All",
      inspiration: "Inspiration",
      sales: "Sales",
      lifestyle: "Lifestyle",
      education: "Education",
      community: "Community",
    },
  },
  inspiration: {
    title: "Ideas and trends",
    subtitle: "AI-suggested topics tailored to your persona",
    sections: {
      trending: "Trending now",
      forYou: "For you",
      reuse: "Recycle past hits",
    },
    use: "Create a post",
  },
  toasts: {
    saved: "Saved successfully",
    scheduled: "Scheduled",
    published: "Published",
    deleted: "Deleted",
    aiApplied: "AI suggestion applied",
    rescheduled: "Rescheduled",
    error: "Something went wrong",
  },
  approval: {
    title: "Approval workflow",
    pending: "Pending approval",
    approve: "Approve",
    reject: "Reject",
    notes: "Notes",
  },
  abTest: {
    title: "A/B variant",
    variantA: "Variant A",
    variantB: "Variant B",
    predicted: "Predicted engagement",
    pickWinner: "Pick winner",
  },
  versions: {
    title: "Version history",
    restore: "Restore",
    minutesAgo: "{{n}} min ago",
    hoursAgo: "{{n}} h ago",
    daysAgo: "{{n}} d ago",
  },
  engagement: {
    title: "Engagement copilot",
    subtitle: "AI-suggested replies for your post comments",
    suggested: "AI suggestion",
    reply: "Reply",
    aiTone: "Matched to your tone",
  },
  utm: {
    title: "UTM & link shortener",
    short: "Short link",
    clicks: "clicks",
  },
};
